import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { submitInterest, errText } from "@/lib/api";
import { Field, TextInput, TextArea, Select, SubmitBar, SuccessPanel, FormShell } from "@/lib/forms";
import { PageHeader } from "@/components/Section";
import { CREATORS } from "@/data/characters";
import { isWindowOpen } from "@/lib/windows";
import { SealedNotice } from "@/components/recruitment/SealedNotice";
import { UnsealReveal } from "@/components/recruitment/UnsealReveal";

const COPY = {
  talent: {
    kicker: "Model / Creative Talent",
    title: ["Get in front", "of the lens."],
    subtitle: "A lightweight interest form for models and creative talent. Full talent applications open with upcoming Issues.",
    label: "Model / Creative Talent interest",
    marker: "serve. pose. repeat ♡",
  },
  mentor: {
    kicker: "Mentor / Industry Professional",
    title: ["Guide the", "next wave."],
    subtitle: "Tell us how you'd like to mentor. Our team will reach out as mentorship slots open around Campaign Labs.",
    label: "Mentor interest",
    marker: "pass it on ✦",
  },
};

export default function ApplyInterest() {
  const { type } = useParams();
  const c = COPY[type] || COPY.talent;
  const [v, setV] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await submitInterest({ type: type === "mentor" ? "mentor" : "talent", ...v });
      setDone(res);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader kicker={c.kicker} titleLines={c.title} subtitle={c.subtitle} />
      <section className="grain-dark bg-onyx py-14 sm:py-20">
        <div className="mx-auto max-w-[720px] px-4 sm:px-6">
          <Link to="/apply" className="mb-6 inline-flex items-center gap-2 font-head text-sm font-bold uppercase tracking-wide text-goldlight transition-colors hover:text-gold" data-testid="back-to-apply">
            <ArrowLeft size={16} /> All paths
          </Link>

          {!isWindowOpen(type === "mentor" ? "mentor" : "talent") ? (
            <SealedNotice kind={type === "mentor" ? "mentor" : "talent"} />
          ) : done ? (
            <SuccessPanel
              title="We got your message. ⭐"
              id={done.id}
              note="Your interest is saved and our team reviews every submission by hand. We'll reach out if there's a fit with an upcoming Issue or Campaign Lab."
              emailNotice
            />
          ) : (
            <>
            <UnsealReveal kind={type === "mentor" ? "mentor" : "talent"} />
            <FormShell marker={c.marker}>
              <form onSubmit={submit} className="space-y-5" data-testid={`interest-form-${type}`}>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Full name" required><TextInput required value={v.name} onChange={(x) => set("name", x)} testId="field-name" /></Field>
                  <Field label="Email" required><TextInput required type="email" value={v.email} onChange={(x) => set("email", x)} testId="field-email" /></Field>
                  <Field label="Phone"><TextInput value={v.phone} onChange={(x) => set("phone", x)} testId="field-phone" /></Field>
                  {type === "mentor" ? (
                    <Field label="Company / studio"><TextInput value={v.organization} onChange={(x) => set("organization", x)} testId="field-organization" /></Field>
                  ) : (
                    <Field label="Agency (if any)"><TextInput value={v.organization} onChange={(x) => set("organization", x)} testId="field-organization" /></Field>
                  )}
                </div>
                {type === "mentor" && (
                  <Field label="Area of expertise"><Select value={v.discipline} onChange={(x) => set("discipline", x)} options={CREATORS.map((cr) => cr.lane)} testId="field-discipline" /></Field>
                )}
                <Field label="Portfolio / social links"><TextInput value={v.links} onChange={(x) => set("links", x)} testId="field-links" /></Field>
                <Field label={type === "mentor" ? "How would you like to mentor?" : "Tell us about your experience & availability"} required>
                  <TextArea required rows={4} value={v.message} onChange={(x) => set("message", x)} testId="field-message" />
                </Field>
                <SubmitBar loading={loading} label="Send It" loadingLabel="SENDING..." />
              </form>
            </FormShell>
            </>
          )}
        </div>
      </section>
    </>
  );
}
